import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import ProductApi from 'apis/ProductApis';
import productType from 'apis/ProductType';
import UnitApi from 'apis/UnitApi';
import { FormFilter, InputForm, SelectForm } from 'components';
import Content from 'layouts/Content';
import { sharedProps } from 'pages/Plant/SaleLine/FilterSaleLinePage';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { ApiResponse, ComboBoxOption } from '../ProductSloc/FilterProductSloc';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { DefaultOptionType } from 'antd/es/select';

const spanCol = 8;

interface IProps {
  defaultFilter: any;
  handleOkFiltered: (values: any) => void;
  handleCancelFiltered: () => void;
}

export default function FilterProduct({
  defaultFilter,
  handleOkFiltered,
  handleCancelFiltered,
}: IProps) {
  const [form] = useForm();
  // const [dataPlant, setDataPlant] = useState<any>([{}]);
  const [dataUnit, setDataUnit] = useState<ComboBoxOption[]>([]);
  const [dataProductType, setDataProductType] = useState<ComboBoxOption[]>([]);
  const [age, setAge] = useState<ILazySelectData>(initLazySelectData);
  const [collection, setCollection] = useState<ComboBoxOption[]>([]);
  const [commodityLine, setCommodityLine] =
    useState<ILazySelectData>(initLazySelectData);

  const onFinish = (values: any) => {
    const ageToArray = Array.isArray(values.age)
      ? values.age.map((num: number) => num.toString())
      : values.age;

    const trimmedValues = {
      ...values,
      id: values.id ? values.id.trim() : undefined,
      name: values.name ? values.name.trim() : undefined,
      age: ageToArray,
      unit: values.unit,
    };
    handleOkFiltered(trimmedValues);
  };

  useEffect(() => {
    form.setFieldsValue(defaultFilter);
  }, [defaultFilter, form]);

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(defaultFilter);

    handleCancelFiltered();
  };

  // const { mutate: getPlant } = useMutation({
  //   mutationFn: () => PlantApi.getAllPlant(),
  //   onSuccess: (res) => {
  //     setDataPlant(
  //       res.data.getPlant.items
  //         ? res.data.getPlant.items.map((item: any) => ({
  //             value: item.id,
  //             label: item.id,
  //           }))
  //         : [],
  //     );
  //   },
  //   onError: (error) => {
  //     console.log(error);
  //   },
  // });

  const { mutate: getAllUnit } = useMutation({
    mutationFn: () => UnitApi.getAllUnit(),
    onSuccess: (res: ApiResponse<{ unit: string }>) => {
      setDataUnit(
        res.data.getProduct.items
          ? res.data.getProduct.items.map((item) => ({
              value: item.unit,
              label: item.unit,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getAllProductType } = useMutation({
    mutationFn: () => productType.getAllProductType(),
    onSuccess: (res: ApiResponse<{ id: string; name: string }>) => {
      setDataProductType(
        res.data.getCode.items
          ? res.data.getCode.items.map((item) => ({
              value: item.id,
              label: item.name,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getAgeComboBox } = useMutation({
    mutationFn: (params: { pageSize: number; age?: number }) =>
      ProductApi.getAgeCombobox(params),
    onSuccess: (res) => {
      if (res?.data?.getProduct)
        setAge({
          options: (res?.data?.getProduct?.items ?? []).map(
            (item: any) =>
              ({
                value: item?.age,
                title: item?.age,
              }) as DefaultOptionType,
          ),
          totalItems: res?.data?.getProduct?.pageInfo?.totalItems ?? 0,
        });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getCollectionCombobox } = useMutation({
    mutationFn: () => ProductApi.getCollectionCombobox(),
    onSuccess: (res: ApiResponse<{ collection: string }>) => {
      setCollection(
        res.data.getProduct.items
          ? res.data.getProduct.items.map((item) => ({
              value: item.collection,
              label: item.collection,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getCommodityLine } = useMutation({
    mutationFn: (params: { pageSize: number; commodityLine?: string }) =>
      ProductApi.getCommodityLineCombobox(params),
    onSuccess: (res) => {
      if (res?.data?.getProduct)
        setCommodityLine({
          options: (res?.data?.getProduct?.items ?? []).map(
            (item: any) =>
              ({
                value: item?.commodityLine,
                title: item?.commodityLine,
              }) as DefaultOptionType,
          ),
          totalItems: res?.data?.getProduct?.pageInfo?.totalItems ?? 0,
        });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onFinish(form.getFieldsValue());
      e.preventDefault();
    }
  };

  useEffect(() => {
    // getPlant();
    getAllUnit();
    getAllProductType();
    getCollectionCombobox();
  }, []);

  const handleSelectScrollAgeChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getAgeComboBox({
        pageSize,
        age: Number(value),
      });
    },
    [],
  );

  const handleSelectScrollCMDChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getCommodityLine({
        pageSize,
        commodityLine: value,
      });
    },
    [],
  );

  return (
    <Content>
      <FormFilter onCancel={handleCancel} form={form} onFinish={onFinish}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          {/* <Col span={spanCol} className="h-[80px]">
            <Form.Item
              initialValue={defaultFilter?.plantId}
              label="Chi nhánh"
              name="plantId"
            >
              <SelectForm
                disabled={!(user?.isAdmin || user?.isHO)}
                placeholder="Chọn chi nhánh"
                options={dataPlant}
              />
            </Form.Item>
          </Col> */}

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Mã sản phẩm" name="id">
              <InputForm
                onKeyDown={handleKeyDown}
                placeholder="Nhập mã sản phẩm"
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tên sản phẩm" name="name">
              <InputForm
                onKeyDown={handleKeyDown}
                placeholder="Nhập tên sản phẩm"
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Loại hàng" name="type">
              <SelectForm
                placeholder="Chọn loại hàng"
                options={dataProductType}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chủng loại" name="ttype">
              <SelectForm placeholder="Chọn chủng loại" options={[]} />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tuổi vàng" name="age">
              {/* <SelectForm
                {...sharedProps}
                placeholder="Chọn tuổi vàng"
                options={age}
              /> */}

              <LazySelectForm
                {...sharedProps}
                data={age}
                placeholder="Chọn tuổi vàng"
                onSelectScrollChange={handleSelectScrollAgeChange}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Dòng hàng" name="commodityLine">
              <LazySelectForm
                {...sharedProps}
                data={commodityLine}
                placeholder="Chọn dòng hàng"
                onSelectScrollChange={handleSelectScrollCMDChange}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Bộ sưu tập" name="collection">
              <SelectForm
                {...sharedProps}
                placeholder="Chọn bộ sưu tập"
                options={collection}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Đơn vị tính" name="unit">
              <SelectForm placeholder="Chọn đơn vị tính" options={dataUnit} />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
}
